import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState, KeyboardEvent, ChangeEvent } from "react";
import Select from "react-select";
import axios from "axios";
import { PaginationState } from "@tanstack/react-table";
import { showError, showSuccess } from "toast/toast";

import GroupInfoCard, { Groups } from "components/groups/GroupInfoCard";
import TeamMembersCard from "components/groups/TeamMembersCard";
import { getAuthCookie } from "../../utils/authCookies";

interface Agent {
  membership_id: string;
  name: string;
  email: string;
  status?: string;
  license_type?: string;
  company_name?: string;
  jobTitle?: string;
  user_id?: string;
}

type Group = {
  _id: string;
  group_id: string;
  group_name: string;
  payment_after_days: number;
  allow_partial_pay: boolean;
  max_credit_limit: number;
  discount_percent: number;
  total_members: number;
  active_members: number;
  allowed_user_ids: Agent[];
  status: string;
  audience?: string; // "internal" or other values
};



const SignageViewGroup = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const token = getAuthCookie("sessionToken");
  const [group, setGroup] = useState<Group | null>(null);
  const [filteredUsers, setFilteredUsers] = useState<Agent[]>([]);
  const [serverTotal, setServerTotal] = useState(0); // optional, if you want server-side totals
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const navigate = useNavigate();
  const location = useLocation();
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  // Legacy react-select user picker (currently commented in JSX). Live “Add members” modal + Enter-to-search lives in `TeamMembersCard`.
  // Add-member search/select
  const [userOptions, setUserOptions] = useState<
    {
      label: string;
      value: string;
      data: { name: string; email: string; id: string };
    }[]
  >([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const userId = getAuthCookie("userId");
  const PAGE_SIZE = 15;
  const organizationId = getAuthCookie("organizationId");
  // Load users for the add-member select (infinite)
  const fetchUsers = async (query: string, pageNumber = 1) => {
    try {
      setIsLoading(true);
      
      // Check if audience is "internal" to call team members API
      if (group?.audience === "internal") {
        // Call team members API
        const params = new URLSearchParams({
          signageFirmID: organizationId || "",
          licenseType: "SIGNAGE_TEAM_MEMBER",
          page: String(pageNumber),
          limit: String(PAGE_SIZE)
        });
        const q = (query || "").trim();
        if (q) params.set("search", q);

        const url = `${process.env.REACT_APP_API_URL}/organization-profiles/search?${params.toString()}`;
        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch team members:", response.statusText);
          return;
        }

        const data = await response.json();
        const list = Array.isArray(data) ? data : data.data ?? data.results ?? [];

        const newData = list.map((u: any) => {
          const fullName = `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.text || "(no name)";
          return {
            label: fullName,
            value: u?.id || u?._id || "",
            data: {
              email: u?.email || "",
              name: fullName,
              id: u?.id || u?._id || "",
            },
          };
        });

        if (pageNumber > 1) setUserOptions(prev => [...prev, ...newData]);
        else setUserOptions(newData);

        const hasMore = Array.isArray(data)
          ? newData.length === PAGE_SIZE
          : data?.pagination?.more ??
          (data?.total ? pageNumber * PAGE_SIZE < data.total : newData.length === PAGE_SIZE);
        
        setHasMore(hasMore);
      } else {
        // Call existing customer-groups API
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/customer-groups/organizations/${organizationId}/users-created-or-linked?query=${encodeURIComponent(query)}&page=${pageNumber}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newData = (res.data?.data || res.data || []).map((user: any) => ({
          ...user,
          data: {
            email: user.label?.match(/\((.*?)\)/)?.[1] || user.email || "",
            name: user.label?.split(" (")[0] || user.name || "",
            id: user.value || user.id || "",
          },
        }));

        if (pageNumber > 1) setUserOptions(prev => [...prev, ...newData]);
        else setUserOptions(newData);

        const total = res.data?.total || 0;
        const hasMore = total ? pageNumber * PAGE_SIZE < total : newData.length === PAGE_SIZE;
        setHasMore(hasMore);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScrollToBottom = () => {
    if (hasMore && !isLoading) {
      const next = page + 1;
      setPage(next);
      fetchUsers(searchInput, next);
    }
  };

  useEffect(() => {
    // Wait for group data to be loaded before fetching users
    if (group) {
      // Reset user options when group changes
      setUserOptions([]);
      setPage(1);
      setHasMore(true);
      setSearchInput("");
      fetchUsers("", 1); // initial 15 users
    }
  }, [group?.audience]); // Re-fetch when audience changes

  const PostData = async () => {
    if (!selectedUser?.data?.id) return;
    const newAgent = selectedUser.data;

    const exists = group?.allowed_user_ids?.some(a => a.user_id === newAgent.id);
    if (exists) {
      showError("Member already added.");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups/${groupId}/memberships`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: newAgent.id }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to add member");

      showSuccess("Member added successfully!");
      navigate(0);
    } catch (err) {
      console.error(err instanceof Error ? err.message : "Unknown error");
      showError("Failed to add member.");
    }
  };

  const fetchGroupData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/customer-groups/${groupId}/members/datatable`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            sort: {
              field: "name",
              order: "asc",
            },
            search: globalFilter, // Use globalFilter for search
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      // Assuming API returns { group, members, total }
      setGroup(data.cards);
      setFilteredUsers(
        (data.data || []).map((m: Agent) => ({
          membership_id: m.membership_id || m.user_id || "",
          name: m.name || "",
          email: m.email || "",
          status: m.status || "",
          license_type: m.license_type || "",
          company_name: m.company_name || "",
          jobTitle: m.jobTitle || "",
        }))
      );
      setServerTotal(data.total ?? (data.data?.length || 0));
    } catch (err) {
      console.error("Error fetching group data:", err);
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (groupId) fetchGroupData();
  }, [groupId, token, globalFilter, pagination.pageIndex, pagination.pageSize]);

  // Function to handle search
  const handleSearch = (searchTerm?: string) => {
    const term = searchTerm !== undefined ? searchTerm : searchInput.trim();
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setGlobalFilter(term);
  };

  // Function to clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setGlobalFilter("");
  };

  // Keep filteredUsers synced when group changes
  // useEffect(() => {
  //   if (group?.allowed_user_ids) setFilteredUsers(group.allowed_user_ids);
  // }, [group]);

  const handleDeleteAgent = async (gid: string, agentId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups/${gid}/memberships/${agentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to delete agent");

      // setGroup(prev =>
      //   prev
      //     ? {
      //       ...prev,
      //       allowed_user_ids: prev.allowed_user_ids?.filter(a => a.user_id !== agentId),
      //     }
      //     : prev
      // );
      fetchGroupData(); // refresh from server
      showSuccess("Agent deleted successfully.");
    } catch (err) {
      showError("Failed to delete agent.");
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };



  if (loading) return <div>Loading Group Data...</div>;
  if (!group) return <div>Group data not found.</div>;

  return (
    <div className="content">
      <div className="container mt-4">
        {/* <div className="bg-white rounded-3 shadow p-4 border position-relative">
          <h2 className="text-center viewheaders mb-3">
            {group?.name} ({group?.allowed_user_ids?.length || 0} agents)
          </h2>
          <hr />
          <div className="row gy-3 mt-4 text-center">
            <div className="col-md-4">
              <p>
                <span className="fw-semibold viewcells">First Name:</span> {group.name}
              </p>
            </div>
            <div className="col-md-4">
              <p>
                <span className="fw-semibold viewcells">Last Name:</span>{" "}
                {group.status === "Active" ? "Active" : "Inactive"}
              </p>
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-md-12 col-lg-12 col-xl-12">
            <div className="card mt-4 card-bg-fill">
              <div className="card-body p-4">
                <div className="col-md-12 d-flex justify-content-between align-items-center mb-3">
                  <h2 className="mb-0">Members</h2>
                  <div className="d-flex" style={{ gap: "8px", minWidth: "400px" }}>
                    <Select
                      className="flex-grow-1"
                      value={selectedUser}
                      options={userOptions}
                      placeholder="Search or select user..."
                      onInputChange={(inputValue) => {
                        setSearchInput(inputValue);
                        // Do not call fetchUsers on every keystroke; prefer Enter/async pattern (see TeamMembersCard add-member modal).
                      }}
                      onChange={(option) => setSelectedUser(option)}
                      isClearable
                      isLoading={isLoading}
                      onMenuScrollToBottom={handleScrollToBottom}
                      filterOption={(option, inputValue) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                    />
                    <button className="btn btn-sm btn-secondary br-8" onClick={PostData}>
                      Add a Member
                    </button>
                  </div>
                </div>

    
                <div className="d-flex justify-content-end mb-3">
                  <div className="input-group" style={{ width: "260px", height: "40px" }}>
                    <span className="input-group-text bg-white border-end-0 p-2 left-radius">
                      <i className="ri-search-line fs-15"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0 right-radius text-ellipsis"
                      placeholder="Search by Name or Email"
                      value={searchInput}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => table.setGlobalFilter(e.target.value)}
                      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Enter") handleSearchSubmit();
                      }}
                      style={{ borderColor: "#cccccc" }}
                    />
                  </div>
                </div>
                <DataTable table={table} />

                
                {table.getRowModel().rows.length === 0 ? (
                  <div
                    className="text-center my-4 text-muted"
                    style={{ fontSize: "1.1rem" }}
                  >
                    No records found
                  </div>
                ) : (
                  <div className="border-bottom pb-3 mb-4 page-container d-block">
                    <PaginationControls table={table} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div> */}
        {/* page header like your reference */}
        {/* page header */}
        <div className="d-flex align-items-center gap-3 mb-4 gm-head">
          <span className="gm-chip" aria-hidden="true">
            <i className="ri-group-line"></i>
          </span>
          <div>
            <h3 className="mb-1">Group Management</h3>
            <div className="text-muted">Manage team members and their access permissions</div>
          </div>
        </div>


        {/* Group Information */}
        <GroupInfoCard
          group={group}
          onUpdated={(updatedGroup) => {
            console.log("Updated group received:", updatedGroup);
            // Merge the updated data with existing group data
            setGroup((prev) => ({
              ...prev,
              group_name: updatedGroup.group_name || prev?.group_name || "",
              status: updatedGroup.status || prev?.status || "Active",
              group_id: prev?.group_id || prev?._id || "",
              _id: prev?._id || "",
              total_members: prev?.total_members || 0,
              active_members: prev?.active_members || 0,
              allowed_user_ids: prev?.allowed_user_ids || [],
              payment_after_days: prev?.payment_after_days || 0,
              allow_partial_pay: prev?.allow_partial_pay || false,
              max_credit_limit: prev?.max_credit_limit || 0,
              discount_percent: prev?.discount_percent || 0,
            }));
          }}
        />

        {/* Team Members */}
        <TeamMembersCard
          groupId={groupId || ""}
          members={filteredUsers}
          totalMembers={serverTotal}
          pagination={pagination}
          onPaginationChange={setPagination}
          onMembersChanged={fetchGroupData}
          onSearch={handleSearch}   // Pass the search function
          audience={group.audience} // Pass audience for conditional API calls
        />
      </div>
      <style>{`
  /* scoped to this page */
  .gm-head .gm-chip{
    width: 40px;
    height: 40px;
    border-radius: 12px;          /* rounded corners like the ref */
    background: #2F6CF5;          /* blue background for the icon */
    color: #fff;                   /* white icon */
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;               /* icon size */
  }
`}</style>

    </div>
  );
};

export default SignageViewGroup;
